import { useRef } from "react";
import { motion } from "framer-motion";
import { useContent } from "@/context/ContentContext";

function Tile({ tile, index }) {
  const ref = useRef(null);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r || !ref.current) return;
    ref.current.style.setProperty("--sx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--sy", `${e.clientY - r.top}px`);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid={`showcase-tile-${index}`}
      initial={{ opacity: 0, y: 40, scale: 0.97 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: 0.06 * index, ease: [0.33, 1, 0.68, 1] }}
      className={`group relative min-h-56 overflow-hidden rounded-2xl border border-gold/15 transition-colors duration-500 hover:border-gold/50 ${tile.span}`}
    >
      <img
        src={tile.src}
        alt={tile.label}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/20 to-transparent" />
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{ background: "radial-gradient(220px circle at var(--sx, 50%) var(--sy, 50%), rgba(212,175,55,0.22), transparent 65%)" }}
      />
      <div className="absolute bottom-4 left-4 flex items-center gap-3">
        <span className="h-px w-6 bg-gold" />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-bright sm:text-xs">{tile.label}</p>
      </div>
    </motion.div>
  );
}

export default function Showcase() {
  const content = useContent();
  const sc = content.showcase;
  return (
    <section id="isler" data-testid="showcase-section" className="relative bg-obsidian py-24 sm:py-32 lg:py-40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-gold"
        >
          {sc.eyebrow}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-6 max-w-3xl font-display text-3xl font-bold tracking-tight text-zinc-100 sm:text-4xl lg:text-5xl"
        >
          {sc.titlePre} <span className="text-gold">{sc.titleGold}</span>
        </motion.h2>

        <div className="mt-14 grid auto-rows-[14rem] grid-cols-1 gap-4 sm:grid-cols-3 lg:auto-rows-[16rem]">
          {sc.tiles.map((t, i) => (
            <Tile key={`${t.label}-${i}`} tile={t} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
